import { useRef } from 'react'
import { Box, Button, Field, HStack, Input, Menu, Portal, Text } from '@chakra-ui/react';
import { MarkdownEditor, type MarkdownEditorApi } from '../lobbyDay/MarkdownEditor'
import { useMergeFields } from '../../hooks/useCommunications'
import type { EmailSourceKind } from '../../types/communications'

interface MergeFieldInsertEditorProps {
  source: EmailSourceKind | undefined
  subject: string
  onSubjectChange: (v: string) => void
  body: string
  onBodyChange: (v: string) => void
}

/** Owns the Subject input + the body MarkdownEditor + the "Insert field ▾" toolbar that splices a
 * `{{Key}}` token into the body at the cursor (spec §8). Subject-field insertion is NOT wired up
 * here -- subject tokens are typed manually (task-10 brief step 2's explicit call); the legend
 * card next to this editor documents that. */
export function MergeFieldInsertEditor({
  source, subject, onSubjectChange, body, onBodyChange,
}: MergeFieldInsertEditorProps) {
  // Holds the insert-at-cursor seam handed up by MarkdownEditor's onReady (Task 9) once the
  // underlying textarea/Tiptap instance exists -- a ref, not state, since it never needs to
  // trigger a re-render on its own.
  const apiRef = useRef<MarkdownEditorApi | null>(null)
  const { data: mergeFields } = useMergeFields(source)
  const hasFields = !!mergeFields && mergeFields.length > 0

  const insertField = (key: string) => {
    apiRef.current?.insertAtCursor(`{{${key}}}`)
  }

  return (
    <Box>
      <Field.Root required mb={3}>
        <Field.Label fontSize="sm" color="fg.muted">Subject</Field.Label>
        <Input
          value={subject}
          onChange={(e) => onSubjectChange(e.target.value)}
          placeholder="Subject line"
        />
      </Field.Root>
      <Text fontSize="xs" color="fg.muted" mb={3}>
        Merge fields work in the subject too — type them manually, e.g. {'{{FirstName}}'}.
      </Text>

      <HStack justify="space-between" mb={2}>
        <Text fontSize="sm" color="fg.muted">Message</Text>
        <Menu.Root positioning={{ placement: 'bottom-end' }}>
          <Menu.Trigger asChild>
            <Button size="sm" variant="outline" disabled={!hasFields}>
              Insert field ▾
            </Button>
          </Menu.Trigger>
          <Portal>
            <Menu.Positioner>
              <Menu.Content>
                {(mergeFields ?? []).map((field) => (
                  <Menu.Item
                    key={field.key}
                    value={field.key}
                    onSelect={() => insertField(field.key)}
                  >
                    {field.label}
                    <Text as="span" ml="auto" fontSize="xs" color="fg.muted">{`{{${field.key}}}`}</Text>
                  </Menu.Item>
                ))}
              </Menu.Content>
            </Menu.Positioner>
          </Portal>
        </Menu.Root>
      </HStack>

      <MarkdownEditor
        value={body}
        onChange={onBodyChange}
        placeholder="Write your message…"
        rows={12}
        onReady={(api) => { apiRef.current = api }}
      />
    </Box>
  );
}
